import { IonAvatar, IonCard, IonHeader, IonPage, IonTitle } from "@ionic/react";
import { useTranslation } from "react-i18next";
import EmptyAvatar from "@/assets/empty_avatar.svg";
import { BackButton } from "@/components/back-button";
import { Buttons } from "@/components/buttons";
import { Content } from "@/components/content";
import { Toolbar } from "@/components/toolbar";
import { useInfiniteConversations } from "@/lib/query/conversation/use-infinite-conversations";
import { useInfiniteMessages } from "@/lib/query/message/use-infinite-messages";
import { Routes } from "@/lib/router";
import { useWebSocketConnection } from "@/lib/websocket/hooks";
import { useStatusBarHeight } from "@/utils/use-status-bar-height";

export function ConversationInfoPage() {
  const { t } = useTranslation();
  const { id } = Routes.ConversationInfo.useParams();
  const statusBarHeight = useStatusBarHeight();

  // Track WebSocket connection status for debugging
  const { isConnected, connectionState } = useWebSocketConnection();

  // Get conversation details
  const { conversations } = useInfiniteConversations();
  const conversation = conversations.find((c) => c.id === id);

  // Get message count
  const { messages } = useInfiniteMessages(id);

  // Get participant info
  const contactParticipant = conversation?.participants?.find((p) => p.side === "CONTACT");
  const displayName =
    contactParticipant?.displayName ||
    contactParticipant?.messageContact?.displayName ||
    conversation?.title ||
    t("routes:conversations.unknown-participant");
  const avatarUrl =
    contactParticipant?.avatarUrl || contactParticipant?.messageContact?.avatarUrl || undefined;
  const phoneNumber =
    contactParticipant?.phoneNumber || contactParticipant?.messageContact?.phoneNumber;
  const email = contactParticipant?.messageContact?.email;

  const channelLabel = {
    WHATSAPP: "WhatsApp",
    SMS: "SMS",
    EMAIL: "Email",
    TELEGRAM: "Telegram",
    WEBCHAT: "Webchat",
  }[conversation?.channel || "WHATSAPP"];

  return (
    <IonPage>
      <IonHeader>
        <Toolbar style={{ paddingTop: `calc(${statusBarHeight}px + 0.5rem)` }}>
          <Buttons slot="start">
            <BackButton defaultHref={id ? Routes.Conversation({ id }) : Routes.Conversations()} />
          </Buttons>
          <IonTitle>{t("routes:conversations.info.title")}</IonTitle>
        </Toolbar>
      </IonHeader>

      <Content className="part-scroll:gap-6">
        {/* Profile section */}
        <div className="flex flex-col items-center gap-4 py-6">
          <IonAvatar className="size-24">
            <img
              src={avatarUrl || EmptyAvatar}
              alt={displayName}
              className="size-full rounded-full object-cover"
            />
          </IonAvatar>
          <div className="text-center">
            <h2 className="text-xl font-bold text-txt-primary">{displayName}</h2>
            {phoneNumber && <p className="text-sm text-txt-secondary">{phoneNumber}</p>}
          </div>
        </div>

        {/* Info cards */}
        <div className="flex flex-col gap-3">
          <h3 className="text-lg font-semibold text-txt-primary">
            {t("routes:conversations.info.general")}
          </h3>

          <IonCard className="flex flex-col gap-2 p-4">
            <InfoRow label={t("routes:conversations.info.channel")} value={channelLabel} />
            {phoneNumber && (
              <InfoRow label={t("routes:conversations.info.phone")} value={phoneNumber} />
            )}
            {email && <InfoRow label={t("routes:conversations.info.email")} value={email} />}
            <InfoRow
              label={t("routes:conversations.info.messages")}
              value={String(messages.length)}
            />
          </IonCard>
        </div>

        {/* WebSocket connection status for debugging */}
        <div className="flex flex-col gap-3">
          <h3 className="text-lg font-semibold text-txt-primary">
            {t("routes:conversations.info.debug", "Debug")}
          </h3>

          <IonCard className="flex flex-col gap-2 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-txt-secondary">WebSocket</span>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-txt-primary">{connectionState}</span>
                <div
                  className={`size-2 rounded-full ${
                    isConnected
                      ? "bg-green-500"
                      : connectionState === "connecting"
                        ? "bg-yellow-500"
                        : "bg-red-500"
                  }`}
                />
              </div>
            </div>
          </IonCard>
        </div>
      </Content>
    </IonPage>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-txt-secondary">{label}</span>
      <span className="text-sm font-medium text-txt-primary">{value}</span>
    </div>
  );
}
